import React from 'react';
import { Container } from 'react-bootstrap';
import { motion, useReducedMotion } from 'framer-motion';
import {
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiCode,
  FiCompass,
  FiMonitor,
  FiUser,
} from 'react-icons/fi';
import styled, { keyframes } from 'styled-components';

const heroCopyVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.09,
    },
  },
};

const heroCopyItemVariants = {
  hidden: {
    opacity: 0,
    y: 28,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.72,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const heroGlowDrift = keyframes`
  from { transform: translate3d(-2%, -2%, 0) scale(0.96); }
  to { transform: translate3d(3%, 4%, 0) scale(1.06); }
`;

const statusPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 4px rgba(69, 180, 126, 0.1); }
  50% { box-shadow: 0 0 0 9px rgba(69, 180, 126, 0); }
`;

const deskGlassSweep = keyframes`
  0%, 18% { left: -38%; }
  55%, 100% { left: 132%; }
`;

const journeyStepFocus = keyframes`
  0%, 22%, 100% {
    border-color: rgba(17, 19, 24, 0.075);
    background: rgba(255, 255, 255, 0.76);
    box-shadow: none;
    transform: translateY(0) scale(1);
  }

  8%, 15% {
    border-color: rgba(71, 133, 211, 0.25);
    background: linear-gradient(135deg, #eef6ff, #e4f0fd);
    box-shadow: 0 13px 28px rgba(59, 111, 172, 0.1);
    transform: translateY(-4px) scale(1.01);
  }
`;

const journeyIconFocus = keyframes`
  0%, 22%, 100% {
    background: #edf2f6;
    color: #657587;
    transform: scale(1);
  }

  8%, 15% {
    background: #397bcf;
    color: #fff;
    transform: scale(1.06);
  }
`;

const journeyMetaFocus = keyframes`
  0%, 22%, 100% {
    background: #f3f5f7;
    color: #7c8793;
  }

  8%, 15% {
    background: rgba(57, 123, 207, 0.1);
    color: #397bcf;
  }
`;

const HeroSection = styled.section`
  position: relative;
  display: flex;
  min-height: max(860px, 100svh);
  align-items: center;
  overflow: hidden;
  padding: 130px 0 70px;
  background:
    linear-gradient(rgba(17, 19, 24, 0.032) 1px, transparent 1px),
    linear-gradient(90deg, rgba(17, 19, 24, 0.032) 1px, transparent 1px),
    #f7f6f2;
  background-size: 56px 56px, 56px 56px, auto;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    z-index: -2;
    inset: 0;
    background:
      linear-gradient(90deg, #f7f6f2 0%, rgba(247, 246, 242, 0.42) 52%, #f7f6f2 100%),
      linear-gradient(180deg, rgba(247, 246, 242, 0.1), transparent 48%, #f7f6f2 100%);
  }

  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    top: 8%;
    right: -10%;
    width: min(52vw, 760px);
    aspect-ratio: 1;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(87, 157, 234, 0.22), rgba(87, 157, 234, 0) 70%);
    animation: ${heroGlowDrift} 16s ease-in-out infinite alternate;
    pointer-events: none;
  }

  @media (max-width: 900px) {
    min-height: 0;
    padding: 118px 0 90px;
  }

  @media (max-width: 620px) {
    padding: 102px 0 64px;
    background-size: 44px 44px, 44px 44px, auto;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(360px, 0.8fr) minmax(570px, 1.2fr);
  align-items: center;
  gap: clamp(40px, 5vw, 84px);

  @media (max-width: 1100px) {
    grid-template-columns: minmax(320px, 0.85fr) minmax(500px, 1.15fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 58px;
  }
`;

const Copy = styled(motion.div)`
  position: relative;
  z-index: 3;
  padding-bottom: 22px;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 28px;
    color: #555b66;
    font-size: 0.78rem;
    font-weight: 760;
    letter-spacing: -0.01em;
  }

  .eyebrow i {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #4074ff;
    box-shadow: 0 0 0 6px rgba(64, 116, 255, 0.11);
  }

  h1 {
    max-width: 690px;
    margin: 0;
    color: #111318;
    font-size: clamp(4rem, 5.9vw, 6rem);
    font-weight: 830;
    line-height: 0.99;
    letter-spacing: -0.07em;
  }

  h1 span {
    display: inline-block;
    padding-right: 0.08em;
    background: linear-gradient(105deg, #111318 5%, #59636f 50%, #3976b8 100%);
    background-clip: text;
    color: transparent;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  > p {
    max-width: 590px;
    margin: 32px 0 0;
    color: #626771;
    font-size: clamp(1.02rem, 1.45vw, 1.2rem);
    font-weight: 480;
    line-height: 1.78;
    letter-spacing: -0.025em;
  }

  > p strong {
    color: #303640;
    font-weight: 720;
  }

  @media (max-width: 620px) {
    padding-bottom: 0;

    .eyebrow {
      margin-bottom: 24px;
      font-size: 0.72rem;
    }

    h1 {
      max-width: 500px;
      font-size: clamp(3.05rem, 14vw, 4.1rem);
      line-height: 1.01;
      letter-spacing: -0.067em;
    }

    > p {
      margin-top: 26px;
      font-size: 1rem;
      line-height: 1.72;
    }

    > p br {
      display: none;
    }
  }
`;

const HeroActions = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 36px;
`;

const HeroButton = styled.a`
  display: inline-flex;
  min-height: 58px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 23px;
  border: 1px solid rgba(17, 19, 24, 0.14);
  border-radius: 17px;
  background: #111318;
  color: #fff !important;
  box-shadow: 0 2px 0 rgba(255, 255, 255, 0.16) inset, 0 14px 30px rgba(17, 19, 24, 0.15);
  font-size: 0.94rem;
  font-weight: 760;
  letter-spacing: -0.015em;
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 240ms ease, background 240ms ease;

  svg {
    transition: transform 220ms ease;
  }

  &:hover,
  &:focus-visible {
    background: #242831;
    color: #fff !important;
    transform: translateY(-3px);
    box-shadow: 0 2px 0 rgba(255, 255, 255, 0.16) inset, 0 19px 40px rgba(17, 19, 24, 0.22);
    outline: none;
  }

  &:hover svg,
  &:focus-visible svg {
    transform: translateX(4px);
  }

  @media (max-width: 460px) {
    width: 100%;
  }
`;

const Assurance = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  margin-top: 28px;
  color: #69717c;
  font-size: 0.74rem;
  font-weight: 650;

  span {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  svg {
    color: #397bcf;
  }
`;

const StudioFrame = styled(motion.div)`
  position: relative;
  z-index: 2;
  min-width: 0;
  padding: clamp(28px, 3vw, 46px);
  border: 1px solid rgba(79, 112, 150, 0.14);
  border-radius: 42px;
  background:
    radial-gradient(circle at 88% 4%, rgba(99, 169, 239, 0.2), transparent 35%),
    linear-gradient(145deg, rgba(255, 255, 255, 0.82), rgba(236, 242, 248, 0.74));
  box-shadow: 0 45px 110px rgba(42, 55, 73, 0.16), inset 0 1px 0 #fff, 0 0 0 10px rgba(255, 255, 255, 0.25);

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    right: -34px;
    bottom: -44px;
    width: 260px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(243, 193, 150, 0.26), rgba(243, 193, 150, 0) 70%);
  }

  @media (max-width: 620px) {
    padding: 12px;
    border-radius: 28px;
    box-shadow: 0 30px 72px rgba(42, 55, 73, 0.16), inset 0 1px 0 #fff, 0 0 0 5px rgba(255, 255, 255, 0.3);
  }
`;

const ApplicationDesk = styled.div`
  position: relative;
  min-height: 570px;
  overflow: hidden;
  border: 1px solid rgba(107, 133, 163, 0.18);
  border-radius: 28px;
  background: rgba(249, 251, 253, 0.94);
  box-shadow: 0 30px 72px rgba(36, 50, 69, 0.16), inset 0 1px 0 #fff;

  &::after {
    content: '';
    position: absolute;
    z-index: 5;
    top: -90%;
    left: -38%;
    width: 20%;
    height: 280%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.58), transparent);
    opacity: 0.68;
    transform: rotate(18deg);
    animation: ${deskGlassSweep} 8s ease-in-out infinite;
    pointer-events: none;
  }

  @media (max-width: 620px) {
    min-height: 510px;
    border-radius: 22px;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      display: none;
      animation: none;
    }
  }
`;

const DeskBar = styled.div`
  display: flex;
  min-height: 62px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px;
  border-bottom: 1px solid rgba(17, 19, 24, 0.08);
  background: rgba(255, 255, 255, 0.72);

  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mark {
    display: grid;
    width: 34px;
    height: 34px;
    place-items: center;
    border-radius: 11px;
    background: #111722;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 850;
  }

  strong {
    display: block;
    color: #202630;
    font-size: 0.7rem;
    font-weight: 820;
    letter-spacing: 0.09em;
  }

  small {
    display: block;
    margin-top: 1px;
    color: #88929e;
    font-size: 0.52rem;
    font-weight: 720;
    letter-spacing: 0.1em;
  }

  .status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 10px;
    border: 1px solid rgba(41, 145, 101, 0.16);
    border-radius: 999px;
    background: #edf8f2;
    color: #2f7f61;
    font-size: 0.62rem;
    font-weight: 780;
  }

  .status i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #45b47e;
    box-shadow: 0 0 0 4px rgba(69, 180, 126, 0.1);
    animation: ${statusPulse} 2.4s ease-in-out infinite;
  }

  @media (max-width: 420px) {
    padding: 0 14px;

    .status span {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .status i {
      animation: none;
    }
  }
`;

const DeskContent = styled.div`
  padding: clamp(20px, 3vw, 34px);

  .desk-kicker {
    color: #7a8795;
    font-size: 0.62rem;
    font-weight: 820;
    letter-spacing: 0.15em;
  }

  h2 {
    max-width: 510px;
    margin: 12px 0 8px;
    color: #171b22;
    font-size: clamp(1.55rem, 3vw, 2.15rem);
    font-weight: 800;
    line-height: 1.12;
    letter-spacing: -0.045em;
  }

  > p {
    margin: 0;
    color: #74808d;
    font-size: 0.8rem;
    font-weight: 560;
    line-height: 1.6;
  }
`;

const JourneyList = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 24px;
`;

const JourneyRow = styled.div`
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 13px;
  min-height: 76px;
  padding: 12px 14px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(71, 133, 211, 0.25)' : 'rgba(17, 19, 24, 0.075)')};
  border-radius: 17px;
  background: ${({ $active }) => ($active
    ? 'linear-gradient(135deg, #eef6ff, #e4f0fd)'
    : 'rgba(255, 255, 255, 0.76)')};
  box-shadow: ${({ $active }) => ($active ? '0 13px 28px rgba(59, 111, 172, 0.1)' : 'none')};
  animation: ${journeyStepFocus} 6s ease-in-out infinite;
  animation-delay: ${({ $delay = 0 }) => `${$delay}s`};
  will-change: transform;

  .icon {
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border-radius: 13px;
    background: ${({ $active }) => ($active ? '#397bcf' : '#edf2f6')};
    color: ${({ $active }) => ($active ? '#fff' : '#657587')};
    font-size: 1rem;
    animation: ${journeyIconFocus} 6s ease-in-out infinite;
    animation-delay: ${({ $delay = 0 }) => `${$delay}s`};
  }

  small {
    display: block;
    color: #8b96a2;
    font-size: 0.57rem;
    font-weight: 780;
    letter-spacing: 0.12em;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: #252b34;
    font-size: 0.88rem;
    font-weight: 760;
  }

  .meta {
    padding: 6px 9px;
    border-radius: 999px;
    background: ${({ $active }) => ($active ? 'rgba(57, 123, 207, 0.1)' : '#f3f5f7')};
    color: ${({ $active }) => ($active ? '#397bcf' : '#7c8793')};
    font-size: 0.62rem;
    font-weight: 780;
    white-space: nowrap;
    animation: ${journeyMetaFocus} 6s ease-in-out infinite;
    animation-delay: ${({ $delay = 0 }) => `${$delay}s`};
  }

  @media (max-width: 420px) {
    grid-template-columns: 38px minmax(0, 1fr);
    min-height: 72px;

    .icon {
      width: 38px;
      height: 38px;
    }

    .meta {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;

    .icon,
    .meta {
      animation: none;
    }
  }
`;

const DeskFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(17, 19, 24, 0.07);
  color: #7a8693;
  font-size: 0.65rem;
  font-weight: 680;

  strong {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #397bcf;
    font-weight: 780;
  }
`;

const Hero = () => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <HeroSection id="home" aria-labelledby="apply-hero-title">
      <Container>
        <HeroGrid>
          <Copy
            variants={heroCopyVariants}
            initial={prefersReducedMotion ? false : 'hidden'}
            animate="visible"
          >
            <motion.div className="eyebrow" variants={heroCopyItemVariants}>
              <i aria-hidden="true" />2026 나니아랩 과정 신청
            </motion.div>
            <motion.h1 id="apply-hero-title" variants={heroCopyItemVariants}>
              상상을 코드로,<br />
              <span>아이디어를 세상으로.</span>
            </motion.h1>
            <motion.p variants={heroCopyItemVariants}>
              AI를 배우는 데서 멈추지 않고,<br />
              <strong>나만의 프로젝트를 직접 완성하고 공개합니다.</strong>
            </motion.p>
            <HeroActions variants={heroCopyItemVariants}>
              <HeroButton href="#regular-course-intro">
                정기과정 살펴보기
                <FiArrowRight aria-hidden="true" />
              </HeroButton>
            </HeroActions>
            <Assurance aria-label="과정 특징" variants={heroCopyItemVariants}>
              <span><FiCompass aria-hidden="true" />문제 찾기</span>
              <span><FiCode aria-hidden="true" />AI와 만들기</span>
              <span><FiMonitor aria-hidden="true" />실제 링크로 공개</span>
            </Assurance>
          </Copy>

          <StudioFrame
            initial={prefersReducedMotion ? false : { opacity: 0, y: 44, rotateX: 5, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
            transition={{
              type: 'spring',
              stiffness: 105,
              damping: 21,
              mass: 0.85,
              delay: 0.3,
            }}
          >
            <ApplicationDesk
              role="img"
              aria-label="무료 오리엔테이션부터 상담과 4주 프로젝트까지 이어지는 나니아랩 신청 과정"
            >
              <DeskBar>
                <div className="brand">
                  <span className="mark">N</span>
                  <span><strong>NARNIA LAB</strong><small>APPLICATION DESK</small></span>
                </div>
                <span className="status"><i aria-hidden="true" /><span>신청 가능</span></span>
              </DeskBar>
              <DeskContent>
                <div className="desk-kicker">YOUR PROJECT JOURNEY</div>
                <h2>아이에게 맞는 시작을<br />한눈에 확인하세요.</h2>
                <p>경험하고, 상담하고, 직접 만드는 과정까지 자연스럽게 이어집니다.</p>
                <JourneyList>
                  <JourneyRow $active $delay={0}>
                    <span className="icon"><FiMonitor aria-hidden="true" /></span>
                    <span><small>STEP 01 · EXPERIENCE</small><strong>무료 온·오프라인 OT</strong></span>
                    <span className="meta">0원</span>
                  </JourneyRow>
                  <JourneyRow $delay={2}>
                    <span className="icon"><FiUser aria-hidden="true" /></span>
                    <span><small>STEP 02 · CONSULT</small><strong>아이에게 맞춘 등록 상담</strong></span>
                    <span className="meta">1:1 안내</span>
                  </JourneyRow>
                  <JourneyRow $delay={4}>
                    <span className="icon"><FiCode aria-hidden="true" /></span>
                    <span><small>STEP 03 · BUILD</small><strong>4주 프로젝트 제작·공개</strong></span>
                    <span className="meta">상담 후 결정</span>
                  </JourneyRow>
                </JourneyList>
                <DeskFooter>
                  <span><FiCalendar aria-hidden="true" /> 일정 선택 후 신청서 작성</span>
                  <strong><FiCheck aria-hidden="true" />계정 생성까지 한 번에</strong>
                </DeskFooter>
              </DeskContent>
            </ApplicationDesk>
          </StudioFrame>
        </HeroGrid>
      </Container>
    </HeroSection>
  );
};

export default Hero;
